import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { v4 as uuidv4 } from 'uuid';
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  User,
  Mail,
  Calendar,
  Clock,
  Users,
  MessageSquare,
  MapPin,
} from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import Button from '../ui/Button';
import InternationalPhoneInput from '../ui/InternationalPhoneInput';
import GuestSelector from '../ui/GuestSelector';
import GuestDropdown from '../ui/GuestDropdown';
import TimeSelector from '../ui/TimeSelector';
import DatePicker from '../ui/DatePicker';
import Stepper, { Step } from '../ui/Stepper';
import ReservationSuccessModal from './ReservationSuccessModal';

interface FormStatus {
  type: 'success' | 'error' | null;
  message: string;
  isSubmitting: boolean;
}

interface ReservationFormProps {
  showStepperOnMobile?: boolean;
  className?: string;
  showMapSection?: boolean;
}

const ReservationForm = ({
  showStepperOnMobile = true,
  className = '',
  showMapSection = false,
}: ReservationFormProps) => {
  const { t } = useLanguage();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    guests: '2',
    note: '',
  });
  const [formStatus, setFormStatus] = useState<FormStatus>({
    type: null,
    message: '',
    isSubmitting: false,
  });
  const [idempotencyKey, setIdempotencyKey] = useState<string>('');

  // The Edge Function returns { success: true } for idempotent replays too, so a
  // double-click would otherwise report one booking as two. Track at most one
  // form-submit per idempotency key; a fresh key is minted after each success.
  const trackedIdempotencyKey = useRef<string | null>(null);

  // Generate new idempotency key on component mount and after successful submission
  useEffect(() => {
    setIdempotencyKey(uuidv4());
  }, []);

  // For mobile stepper validation
  const [mobileStep, setMobileStep] = useState(1);

  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return formData.fullName.trim() !== '' && formData.email.trim() !== '';
      case 2:
        return (
          formData.guests.trim() !== '' &&
          formData.date.trim() !== '' &&
          formData.time.trim() !== ''
        );
      case 3:
        return true; // optional note
      default:
        return false;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formStatus.type) setFormStatus({ type: null, message: '', isSubmitting: false });
  };

  const handlePhoneChange = (value: string) => {
    setFormData(prev => ({ ...prev, phone: value }));
    if (formStatus.type) setFormStatus({ type: null, message: '', isSubmitting: false });
  };

  const handleGuestsChange = (value: string) => {
    setFormData(prev => ({ ...prev, guests: value }));
    if (formStatus.type) setFormStatus({ type: null, message: '', isSubmitting: false });
  };

  const handleTimeChange = (value: string) => {
    setFormData(prev => ({ ...prev, time: value }));
    if (formStatus.type) setFormStatus({ type: null, message: '', isSubmitting: false });
  };

  // Resize listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Smart defaults: tomorrow date + 2 guests
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newIdempotencyKey = uuidv4();
    setFormData(prev => ({
      ...prev,
      date: tomorrow.toISOString().split('T')[0],
      guests: '2',
    }));
    setIdempotencyKey(newIdempotencyKey);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formStatus.isSubmitting) return;
    setFormStatus({ type: null, message: '', isSubmitting: true });

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) throw new Error('Supabase URL not configured');

      // Use development function if enabled
      const functionName =
        import.meta.env.VITE_USE_DEV_RESERVATION_EMAIL === 'true'
          ? 'send-reservation-email-dev'
          : 'send-reservation-email';

      const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKey,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          name: formData.fullName.trim(),
          email: formData.email.trim(),
          phone: formData.phone || undefined,
          date: formData.date,
          time: formData.time,
          guests: formData.guests,
          specialRequests: formData.note.trim() || undefined,
        }),
      });

      const result = await response.json();
      if (response.ok && result.success) {
        if (trackedIdempotencyKey.current !== idempotencyKey) {
          trackedIdempotencyKey.current = idempotencyKey;
          window.umami?.track('form-submit', {
            form: 'reservation',
            source: window.location.pathname,
          });
        }

        setFormStatus({
          type: 'success',
          message: result.message || t.home.reservation.form.confirmation,
          isSubmitting: false,
        });
        setShowSuccessModal(true);
        // Generate new idempotency key for next submission
        setIdempotencyKey(uuidv4());
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
          time: '',
          guests: '2',
          note: '',
        });
      } else {
        throw new Error(result.error || 'Failed to send reservation request');
      }
    } catch (error) {
      console.error('Error sending reservation request:', error);
      setFormStatus({
        type: 'error',
        message: error instanceof Error ? error.message : t.home.reservation.form.errorMessage,
        isSubmitting: false,
      });
      // Keep the same idempotency key for potential retry
    }
  };

  const handleStepComplete = () => {
    // Only submit when final step is reached
    handleSubmit(new Event('submit') as React.FormEvent);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setFormStatus({ type: null, message: '', isSubmitting: false });
  };

  // Determine which form layout to show
  const shouldShowStepper = isMobile && showStepperOnMobile;
  const shouldShowFlatForm = !isMobile || !showStepperOnMobile;

  return (
    <>
      <div className={`w-full ${className}`}>
        {/* Form Status - Show above both form types */}
        {formStatus.type && !showSuccessModal && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg flex items-center gap-3 mb-6 ${
              formStatus.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {formStatus.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
            )}
            <p className="text-sm">{formStatus.message}</p>
          </motion.div>
        )}

        {/* Desktop/Flat Form */}
        {shouldShowFlatForm && (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-lg shadow-lg p-4 sm:p-6 lg:p-8 w-full mx-auto max-w-lg lg:max-w-none"
          >
            {/* Guest Info */}
            <div className="space-y-6">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 mb-4">
                  <User className="w-5 h-5 text-red-800" />
                  {t.home.reservation.form.guestInfoTitle}
                </h3>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fullName" className="block text-neutral-700 mb-2 font-medium">
                      {t.home.reservation.form.name}*
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        disabled={formStatus.isSubmitting}
                        className="w-full px-4 py-3 pl-12 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
                        placeholder={t.home.reservation.form.placeholders.name}
                        autoComplete="name"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        <User className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="email" className="block text-neutral-700 mb-2 font-medium">
                        {t.home.reservation.form.email}*
                      </label>
                      <div className="relative">
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={formStatus.isSubmitting}
                          className="w-full px-4 py-3 pl-12 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
                          placeholder={t.home.reservation.form.placeholders.email}
                          autoComplete="email"
                        />
                        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                          <Mail className="w-5 h-5" />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-neutral-700 mb-2 font-medium">
                        {t.home.reservation.form.phone}
                      </label>
                      <InternationalPhoneInput
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        disabled={formStatus.isSubmitting}
                        placeholder=""
                        containerClassName="flex items-center border border-neutral-300 rounded-md overflow-hidden"
                        buttonClassName="w-12 h-12 flex items-center justify-center border-r border-neutral-300 bg-white"
                        flagClassName="w-8 h-8 rounded-full"
                        dropdownClassName="hidden"
                        inputClassName="flex-1 px-4 py-3 text-sm sm:text-base placeholder-neutral-400"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-neutral-200"></div>

              {/* Reservation Details */}
              <div>
                <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 mb-4">
                  <Calendar className="w-5 h-5 text-red-800" />
                  {t.home.reservation.form.reservationDetailsTitle}
                </h3>

                <div className="space-y-6">
                  <div>
                    <label className="block text-neutral-700 mb-3 font-medium">
                      <Users className="w-4 h-4 inline mr-2" />
                      {t.home.reservation.form.guests}*
                    </label>
                    <GuestSelector
                      value={formData.guests}
                      onChange={handleGuestsChange}
                      disabled={formStatus.isSubmitting}
                    />
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="date" className="block text-neutral-700 mb-3 font-medium">
                        <Calendar className="w-4 h-4 inline mr-2" />
                        {t.home.reservation.form.date}*
                      </label>
                      <DatePicker
                        value={formData.date}
                        onChange={value => setFormData(prev => ({ ...prev, date: value }))}
                        min={new Date().toISOString().split('T')[0]}
                        required
                        disabled={formStatus.isSubmitting}
                        placeholder={t.home.reservation.form.placeholders.date}
                        minDate={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div>
                      <label className="block text-neutral-700 mb-3 font-medium">
                        <Clock className="w-4 h-4 inline mr-2" />
                        {t.home.reservation.form.time}*
                      </label>
                      <TimeSelector
                        value={formData.time}
                        onChange={handleTimeChange}
                        disabled={formStatus.isSubmitting}
                        required
                        placeholder={t.home.reservation.form.timeOptions.select}
                        selectedDate={formData.date}
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="note" className="block text-neutral-700 mb-2 font-medium">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      {t.home.reservation.form.note}
                    </label>
                    <textarea
                      id="note"
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      rows={3}
                      disabled={formStatus.isSubmitting}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base resize-none"
                      placeholder={t.home.reservation.form.placeholders.note}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-neutral-200">
              <Button
                type="submit"
                variant="primary"
                disabled={formStatus.isSubmitting}
                className="w-full text-lg py-4"
              >
                {formStatus.isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.home.reservation.form.submitting}
                  </span>
                ) : (
                  t.home.reservation.form.button
                )}
              </Button>
              {!formStatus.type && (
                <p className="text-xs sm:text-sm text-neutral-500 mt-4 text-center leading-relaxed">
                  {t.home.reservation.form.defaultNote}
                </p>
              )}
            </div>
          </form>
        )}

        {/* Mobile Stepper */}
        {shouldShowStepper && (
          <div className="bg-white rounded-lg shadow-lg w-full mx-auto">
            <Stepper
              initialStep={1}
              onStepChange={step => setMobileStep(step)}
              onFinalStepCompleted={handleStepComplete}
              backButtonText={
                t.home.reservation.form.button === 'Request Reservation' ? 'Back' : 'Til baka'
              }
              nextButtonText={
                t.home.reservation.form.button === 'Request Reservation' ? 'Next' : 'Áfram'
              }
              disableStepIndicators={true}
              stepCircleContainerClassName="bg-white border-0 shadow-none"
              contentClassName="px-3"
              footerClassName="px-3"
              backButtonProps={{ disabled: formStatus.isSubmitting }}
              nextButtonProps={{
                disabled: !isStepValid(mobileStep) || formStatus.isSubmitting,
              }}
            >
              {/* Step 1: Guest Info */}
              <Step>
                <div className="space-y-4 py-2">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 mb-4">
                    <User className="w-5 h-5 text-red-800" />
                    <span className="text-base">{t.home.reservation.form.guestInfoTitle}</span>
                  </h3>

                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-neutral-700 mb-2 font-medium text-sm"
                    >
                      {t.home.reservation.form.name}*
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        disabled={formStatus.isSubmitting}
                        className="w-full px-4 py-3 pl-12 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
                        placeholder={t.home.reservation.form.placeholders.name}
                        autoComplete="name"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        <User className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-neutral-700 mb-2 font-medium text-sm"
                    >
                      {t.home.reservation.form.email}*
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        disabled={formStatus.isSubmitting}
                        className="w-full px-4 py-3 pl-12 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base"
                        placeholder={t.home.reservation.form.placeholders.email}
                        autoComplete="email"
                      />
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-500">
                        <Mail className="w-5 h-5" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-neutral-700 mb-2 font-medium text-sm">
                      {t.home.reservation.form.phone}
                    </label>
                    <InternationalPhoneInput
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      disabled={formStatus.isSubmitting}
                      placeholder=""
                    />
                  </div>
                </div>
              </Step>

              {/* Step 2: Reservation Details */}
              <Step>
                <div className="space-y-4 py-2">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 mb-4">
                    <Calendar className="w-5 h-5 text-red-800" />
                    <span className="text-base">
                      {t.home.reservation.form.reservationDetailsTitle}
                    </span>
                  </h3>

                  <div>
                    <label className="block text-neutral-700 mb-2 font-medium text-sm">
                      <Users className="w-4 h-4 inline mr-2" />
                      {t.home.reservation.form.guests}*
                    </label>
                    <GuestDropdown
                      value={formData.guests}
                      onChange={handleGuestsChange}
                      disabled={formStatus.isSubmitting}
                      placeholder={
                        t.home.reservation.form.placeholders.guests || 'Select number of guests'
                      }
                      required
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="date"
                      className="block text-neutral-700 mb-2 font-medium text-sm"
                    >
                      <Calendar className="w-4 h-4 inline mr-2" />
                      {t.home.reservation.form.date}*
                    </label>
                    <DatePicker
                      value={formData.date}
                      onChange={value => setFormData(prev => ({ ...prev, date: value }))}
                      min={new Date().toISOString().split('T')[0]}
                      required
                      disabled={formStatus.isSubmitting}
                      placeholder={t.home.reservation.form.placeholders.date || 'Select date'}
                      minDate={new Date().toISOString().split('T')[0]}
                    />
                  </div>

                  <div>
                    <label className="block text-neutral-700 mb-2 font-medium text-sm">
                      <Clock className="w-4 h-4 inline mr-2" />
                      {t.home.reservation.form.time}*
                    </label>
                    <TimeSelector
                      value={formData.time}
                      onChange={handleTimeChange}
                      disabled={formStatus.isSubmitting}
                      required
                      placeholder={t.home.reservation.form.timeOptions.select || 'Select time'}
                      selectedDate={formData.date}
                    />
                  </div>
                </div>
              </Step>

              {/* Step 3: Special Requests */}
              <Step>
                <div className="space-y-4 py-2">
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-900 mb-4">
                    <MessageSquare className="w-5 h-5 text-red-800" />
                    <span className="text-base">{t.home.reservation.form.note}</span>
                  </h3>

                  <div>
                    <textarea
                      id="note"
                      name="note"
                      value={formData.note}
                      onChange={handleChange}
                      rows={4}
                      disabled={formStatus.isSubmitting}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 text-sm sm:text-base resize-none"
                      placeholder={t.home.reservation.form.placeholders.note}
                    />
                  </div>

                  <div className="pt-2">
                    <p className="text-xs text-neutral-500 mt-4 text-center leading-relaxed">
                      {t.home.reservation.form.defaultNote}
                    </p>
                  </div>

                  {formStatus.isSubmitting && (
                    <div className="flex justify-center items-center pt-4">
                      <Loader2 className="w-6 h-6 animate-spin text-red-800" />
                      <span className="ml-2">{t.home.reservation.form.submitting}</span>
                    </div>
                  )}
                </div>
              </Step>
            </Stepper>
          </div>
        )}

        {/* Optional Map Section */}
        {showMapSection && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="w-full mt-8 lg:mt-0"
          >
            <div className="h-full relative rounded-lg shadow-md overflow-hidden min-h-[400px] lg:min-h-[600px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1739.959338569762!2d-21.928471483883666!3d64.14469761638455!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48d674c7c735704b%3A0xcddb30a944ea193b!2sRossopomodoro!5e0!3m2!1sen!2sus!4v1749820966602!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                title="Rossopomodoro Restaurant Location in downtown Reykjavik"
                className="absolute inset-0"
              />
              <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white px-2 sm:px-4 py-1 sm:py-2 rounded-lg shadow-md flex items-center gap-1 sm:gap-2">
                <MapPin className="text-red-800" size={20} />
                <a
                  href="https://maps.app.goo.gl/iJ38nHTkanTaXeiS8"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="text-sm text-neutral-700 hover:text-red-800 transition-colors"
                  aria-label="View Rossopomodoro location on Google Maps - Best Italian restaurant in downtown Reykjavik"
                >
                  Laugavegi 40a, 101 Reykjavík
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Success Modal */}
      <ReservationSuccessModal
        isVisible={showSuccessModal}
        onClose={handleCloseSuccessModal}
        formStatus={formStatus}
        translations={t}
      />
    </>
  );
};

export default ReservationForm;
